// SolarInstallationQuote.js - Solar installation themed detailed quote form component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a solar installation themed detailed quote form section
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the quote section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The quote section elements
 */
export const createSolarInstallationQuoteElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const quoteSectionClass = 'solar-quote-section';
  const quoteContainerClass = 'solar-quote-container';
  const quoteHeaderClass = 'solar-quote-header';
  const quoteTitleClass = 'solar-quote-title';
  const quoteSubtitleClass = 'solar-quote-subtitle';
  const quoteFormWrapperClass = 'solar-quote-form-wrapper';
  const quoteFormClass = 'solar-quote-form';
  const quoteStepClass = 'solar-quote-step';
  const quoteStepTitleClass = 'solar-quote-step-title';
  const quoteFormGroupClass = 'solar-quote-form-group';
  const quoteFormRowClass = 'solar-quote-form-row';
  const quoteFormLabelClass = 'solar-quote-form-label';
  const quoteFormInputClass = 'solar-quote-form-input';
  const quoteFormSelectClass = 'solar-quote-form-select';
  const quoteFormOptionClass = 'solar-quote-form-option';
  const quoteFormTextareaClass = 'solar-quote-form-textarea';
  const quoteFormCheckboxClass = 'solar-quote-form-checkbox';
  const quoteFormButtonClass = 'solar-quote-form-button';
  const quoteFormSuccessClass = 'solar-quote-form-success';
  const quoteFormErrorClass = 'solar-quote-form-error';
  
  // Desktop Styles - Solar themed with whitish background
  const quoteSectionStyles = {
    position: 'relative',
    paddingTop: '100px',
    paddingBottom: '100px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#f8fafc',
    background: `
      radial-gradient(circle at 20% 80%, rgba(74, 222, 128, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(34, 197, 94, 0.03) 0%, transparent 50%),
      linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #f8fafc 100%)
    `,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const quoteContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '900px',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    gap: '40px'
  };

  const quoteHeaderStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: '20px',
    width: '100%'
  };

  const quoteTitleStyles = {
    fontSize: '48px',
    fontWeight: '700',
    color: '#0f2341',
    marginBottom: '20px',
    fontFamily: '"Inter", sans-serif',
    letterSpacing: '-0.02em',
    lineHeight: '1.2'
  };

  const quoteSubtitleStyles = {
    fontSize: '20px',
    color: '#475569',
    lineHeight: '1.6',
    marginBottom: '30px',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '400',
    maxWidth: '700px'
  };

  const quoteFormWrapperStyles = {
    width: '100%',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    border: '1px solid rgba(74, 222, 128, 0.2)',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
    padding: '50px',
    position: 'relative',
    overflow: 'hidden'
  };

  const quoteFormStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '40px',
    width: '100%'
  };

  const quoteStepStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '25px',
    padding: '30px',
    backgroundColor: '#f8fafc',
    borderRadius: '12px',
    border: '1px solid rgba(74, 222, 128, 0.1)'
  };

  const quoteStepTitleStyles = {
    fontSize: '24px',
    fontWeight: '600',
    color: '#0f2341',
    fontFamily: '"Inter", sans-serif',
    marginBottom: '10px',
    paddingBottom: '10px',
    borderBottom: '2px solid #4ade80'
  };

  const quoteFormGroupStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  };

  const quoteFormRowStyles = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px'
  };

  const quoteFormLabelStyles = {
    fontSize: '16px',
    fontWeight: '600',
    color: '#0f2341',
    fontFamily: '"Inter", sans-serif'
  };

  const quoteFormInputStyles = {
    padding: '14px 16px',
    borderRadius: '8px',
    border: '2px solid #e2e8f0',
    fontSize: '16px',
    color: '#0f2341',
    backgroundColor: '#ffffff',
    fontFamily: '"Inter", sans-serif',
    transition: 'all 0.3s ease',
    outline: 'none'
  };

  const quoteFormSelectStyles = {
    padding: '14px 16px',
    borderRadius: '8px',
    border: '2px solid #e2e8f0',
    fontSize: '16px',
    color: '#0f2341',
    backgroundColor: '#ffffff',
    fontFamily: '"Inter", sans-serif',
    transition: 'all 0.3s ease',
    outline: 'none',
    cursor: 'pointer'
  };

  const quoteFormOptionStyles = {
    padding: '8px 12px',
    fontSize: '16px',
    color: '#0f2341',
    backgroundColor: '#ffffff',
    fontFamily: '"Inter", sans-serif'
  };

  const quoteFormTextareaStyles = {
    padding: '14px 16px',
    borderRadius: '8px',
    border: '2px solid #e2e8f0',
    fontSize: '16px',
    color: '#0f2341',
    backgroundColor: '#ffffff',
    fontFamily: '"Inter", sans-serif',
    minHeight: '100px',
    resize: 'vertical',
    transition: 'all 0.3s ease',
    outline: 'none'
  };

  const quoteFormCheckboxStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px',
    cursor: 'pointer'
  };

  const quoteFormButtonStyles = {
    padding: '18px 40px',
    borderRadius: '8px',
    fontSize: '18px',
    fontWeight: '600',
    backgroundColor: '#4ade80',
    color: '#ffffff',
    border: 'none',
    cursor: 'pointer',
    marginTop: '20px',
    transition: 'all 0.3s ease',
    fontFamily: '"Inter", sans-serif',
    boxShadow: '0 4px 15px rgba(74, 222, 128, 0.3)',
    alignSelf: 'center'
  };

  const quoteFormSuccessStyles = {
    padding: '20px',
    backgroundColor: '#dcfce7',
    color: '#166534',
    borderRadius: '8px',
    marginTop: '20px',
    display: 'none',
    border: '1px solid #4ade80',
    fontFamily: '"Inter", sans-serif',
    textAlign: 'center'
  };

  const quoteFormErrorStyles = {
    padding: '20px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
    marginTop: '20px',
    display: 'none',
    border: '1px solid #ef4444',
    fontFamily: '"Inter", sans-serif',
    textAlign: 'center'
  };

  // Mobile styles
  const mobileQuoteFormRowStyles = {
    gridTemplateColumns: '1fr',
    gap: '15px'
  };

  const mobileQuoteFormWrapperStyles = {
    padding: '30px 20px'
  };

  const mobileQuoteTitleStyles = {
    fontSize: '36px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(quoteSectionClass, quoteSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(quoteContainerClass, quoteContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(quoteHeaderClass, quoteHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(quoteTitleClass, quoteTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(quoteSubtitleClass, quoteSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(quoteFormWrapperClass, quoteFormWrapperStyles);
    newRegistry[DEVICES.DESKTOP].set(quoteFormClass, quoteFormStyles);
    newRegistry[DEVICES.DESKTOP].set(quoteStepClass, quoteStepStyles);
    newRegistry[DEVICES.DESKTOP].set(quoteStepTitleClass, quoteStepTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(quoteFormGroupClass, quoteFormGroupStyles);
    newRegistry[DEVICES.DESKTOP].set(quoteFormRowClass, quoteFormRowStyles);
    newRegistry[DEVICES.DESKTOP].set(quoteFormLabelClass, quoteFormLabelStyles);
    newRegistry[DEVICES.DESKTOP].set(quoteFormInputClass, quoteFormInputStyles);
    newRegistry[DEVICES.DESKTOP].set(quoteFormSelectClass, quoteFormSelectStyles);
    newRegistry[DEVICES.DESKTOP].set(quoteFormOptionClass, quoteFormOptionStyles);
    newRegistry[DEVICES.DESKTOP].set(quoteFormTextareaClass, quoteFormTextareaStyles);
    newRegistry[DEVICES.DESKTOP].set(quoteFormCheckboxClass, quoteFormCheckboxStyles);
    newRegistry[DEVICES.DESKTOP].set(quoteFormButtonClass, quoteFormButtonStyles);
    newRegistry[DEVICES.DESKTOP].set(quoteFormSuccessClass, quoteFormSuccessStyles);
    newRegistry[DEVICES.DESKTOP].set(quoteFormErrorClass, quoteFormErrorStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(quoteFormRowClass, mobileQuoteFormRowStyles);
    newRegistry[DEVICES.MOBILE].set(quoteFormWrapperClass, mobileQuoteFormWrapperStyles);
    newRegistry[DEVICES.MOBILE].set(quoteTitleClass, mobileQuoteTitleStyles);

    return newRegistry;
  });
  
  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    quoteSectionClass,
    quoteContainerClass,
    quoteHeaderClass,
    quoteTitleClass,
    quoteSubtitleClass,
    quoteFormWrapperClass,
    quoteFormClass,
    quoteStepClass,
    quoteStepTitleClass,
    quoteFormGroupClass,
    quoteFormRowClass,
    quoteFormLabelClass,
    quoteFormInputClass,
    quoteFormSelectClass,
    quoteFormOptionClass,
    quoteFormTextareaClass,
    quoteFormCheckboxClass,
    quoteFormButtonClass,
    quoteFormSuccessClass,
    quoteFormErrorClass
  ]));
  
  // Generate IDs for elements
  const sectionId = Date.now();
  const containerId = sectionId + 1;
  const headerId = sectionId + 2;
  const titleId = sectionId + 3;
  const subtitleId = sectionId + 4;
  const formWrapperId = sectionId + 5;
  const formId = sectionId + 6;
  
  // Step 1 - Personal Information
  const step1Id = sectionId + 10;
  const step1TitleId = sectionId + 11;
  const nameGroupId = sectionId + 12;
  const nameLabelId = sectionId + 13;
  const nameInputId = sectionId + 14;
  const emailGroupId = sectionId + 15;
  const emailLabelId = sectionId + 16;
  const emailInputId = sectionId + 17;
  const phoneGroupId = sectionId + 18;
  const phoneLabelId = sectionId + 19;
  const phoneInputId = sectionId + 20;
  const addressGroupId = sectionId + 21;
  const addressLabelId = sectionId + 22;
  const addressInputId = sectionId + 23;
  
  // Step 2 - Property Information
  const step2Id = sectionId + 30;
  const step2TitleId = sectionId + 31;
  const propertyTypeGroupId = sectionId + 32;
  const propertyTypeLabelId = sectionId + 33;
  const propertyTypeSelectId = sectionId + 34;
  const roofTypeGroupId = sectionId + 35;
  const roofTypeLabelId = sectionId + 36;
  const roofTypeSelectId = sectionId + 37;
  const propertyAgeGroupId = sectionId + 38;
  const propertyAgeLabelId = sectionId + 39;
  const propertyAgeSelectId = sectionId + 40;
  const roofAreaGroupId = sectionId + 41;
  const roofAreaLabelId = sectionId + 42;
  const roofAreaInputId = sectionId + 43;
  
  // Step 3 - Energy Requirements
  const step3Id = sectionId + 50;
  const step3TitleId = sectionId + 51;
  const monthlyBillGroupId = sectionId + 52;
  const monthlyBillLabelId = sectionId + 53;
  const monthlyBillInputId = sectionId + 54;
  const energyUsageGroupId = sectionId + 55;
  const energyUsageLabelId = sectionId + 56;
  const energyUsageInputId = sectionId + 57;
  const systemSizeGroupId = sectionId + 58;
  const systemSizeLabelId = sectionId + 59;
  const systemSizeSelectId = sectionId + 60;
  
  // Step 4 - Additional Services
  const step4Id = sectionId + 70;
  const step4TitleId = sectionId + 71;
  const servicesGroupId = sectionId + 72;
  const servicesLabelId = sectionId + 73;
  const cctvCheckboxId = sectionId + 74;
  const doorLockCheckboxId = sectionId + 75;
  const intercomCheckboxId = sectionId + 76;
  const maintenanceCheckboxId = sectionId + 77;
  const notesGroupId = sectionId + 78;
  const notesLabelId = sectionId + 79;
  const notesTextareaId = sectionId + 80;
  
  // Submit button and messages
  const submitButtonId = sectionId + 90;
  const successMessageId = sectionId + 91;
  const errorMessageId = sectionId + 92;

  // Additional Services Checkbox Elements
  const cctvInputId = sectionId + 140;
  const cctvSpanId = sectionId + 141;
  const doorLockInputId = sectionId + 142;
  const doorLockSpanId = sectionId + 143;
  const intercomInputId = sectionId + 144;
  const intercomSpanId = sectionId + 145;
  const maintenanceInputId = sectionId + 146;
  const maintenanceSpanId = sectionId + 147;

  // Property Type Select Options
  const propertyTypeOption0Id = sectionId + 99; // Default empty option
  const propertyTypeOption1Id = sectionId + 100;
  const propertyTypeOption2Id = sectionId + 101;
  const propertyTypeOption3Id = sectionId + 102;
  const propertyTypeOption4Id = sectionId + 103;
  const propertyTypeOption5Id = sectionId + 104;
  const propertyTypeOption6Id = sectionId + 105;
  const propertyTypeOption7Id = sectionId + 106;
  const propertyTypeOption8Id = sectionId + 107;

  // Roof Type Select Options
  const roofTypeOption0Id = sectionId + 109; // Default empty option
  const roofTypeOption1Id = sectionId + 110;
  const roofTypeOption2Id = sectionId + 111;
  const roofTypeOption3Id = sectionId + 112;
  const roofTypeOption4Id = sectionId + 113;
  const roofTypeOption5Id = sectionId + 114;
  const roofTypeOption6Id = sectionId + 115;
  const roofTypeOption7Id = sectionId + 116;

  // Property Age Select Options
  const propertyAgeOption0Id = sectionId + 119; // Default empty option
  const propertyAgeOption1Id = sectionId + 120;
  const propertyAgeOption2Id = sectionId + 121;
  const propertyAgeOption3Id = sectionId + 122;
  const propertyAgeOption4Id = sectionId + 123;

  // System Size Select Options
  const systemSizeOption0Id = sectionId + 129; // Default empty option
  const systemSizeOption1Id = sectionId + 130;
  const systemSizeOption2Id = sectionId + 131;
  const systemSizeOption3Id = sectionId + 132;
  const systemSizeOption4Id = sectionId + 133;
  const systemSizeOption5Id = sectionId + 134;
  const systemSizeOption6Id = sectionId + 135;
  const systemSizeOption7Id = sectionId + 136;

  const newElements = [
    // Main Quote Section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Solar Quote Section',
      parentId: null,
      className: quoteSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'quote-section',
        'data-component': 'section',
        'data-section-type': 'quote',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Quote Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: quoteContainerClass,
      styles: {},
      children: [headerId, formWrapperId],
      text: '',
      attributes: {
        'data-element': 'quote-container'
      }
    },
    
    // Header Section
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Quote Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: quoteHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'quote-header'
      }
    },
    
    // Title 
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h1',
      name: 'Quote Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h1'),
      className: quoteTitleClass,
      styles: {},
      children: [],
      text: 'Get Your Free Solar Quote',
      attributes: {
        'data-element': 'quote-title'
      }
    },
    
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Quote Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: quoteSubtitleClass,
      styles: {},
      children: [],
      text: 'Fill out this detailed form to receive a customized solar installation quote. Our experts will analyze your requirements and provide you with the best solution for your energy needs.',
      attributes: {
        'data-element': 'quote-subtitle'
      }
    },
    
    // Form Wrapper
    {
      id: formWrapperId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Quote Form Wrapper',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: quoteFormWrapperClass,
      styles: {},
      children: [formId, successMessageId, errorMessageId],
      text: '',
      attributes: {
        'data-element': 'quote-form-wrapper'
      }
    },
    
    // Form
    {
      id: formId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'form',
      name: 'Quote Form',
      parentId: formWrapperId,
      hierarchyName: getHierarchyName('form'),
      className: quoteFormClass,
      styles: {},
      children: [step1Id, step2Id, step3Id, step4Id, submitButtonId],
      method: 'post',
      action: '',
      text: '',
      attributes: {
        'data-element': 'quote-form',
        'data-form-type': 'quote'
      }
    },
    
    // Step 1 - Personal Information
    {
      id: step1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Step 1 - Personal Information',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: quoteStepClass,
      styles: {},
      children: [step1TitleId, nameGroupId, emailGroupId, phoneGroupId, addressGroupId],
      text: '',
      attributes: {
        'data-element': 'quote-step',
        'data-step': '1'
      }
    },
    
    // Step 1 Title
    {
      id: step1TitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Step 1 Title',
      parentId: step1Id,
      hierarchyName: getHierarchyName('h3'),
      className: quoteStepTitleClass,
      styles: {},
      children: [],
      text: '1. Personal Information',
      attributes: {
        'data-element': 'quote-step-title'
      }
    },
    
    // Name Group
    {
      id: nameGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Name Group',
      parentId: step1Id,
      hierarchyName: getHierarchyName('div'),
      className: quoteFormGroupClass,
      styles: {},
      children: [nameLabelId, nameInputId],
      text: '',
      attributes: {
        'data-element': 'form-group'
      }
    },
    
    // Name Label
    {
      id: nameLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Name Label',
      parentId: nameGroupId,
      hierarchyName: getHierarchyName('label'),
      className: quoteFormLabelClass,
      styles: {},
      children: [],
      text: 'Full Name *',
      attributes: {
        'data-element': 'form-label',
        'for': 'quote-name'
      }
    },
    
    // Name Input
    {
      id: nameInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Name Input',
      parentId: nameGroupId,
      hierarchyName: getHierarchyName('input'),
      className: quoteFormInputClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'form-input',
        'type': 'text',
        'id': 'quote-name',
        'name': 'name',
        'placeholder': 'Enter your full name',
        'required': 'true'
      }
    }
  ];

  // Add remaining form elements (email, phone, address, etc.)
  const additionalElements = [
    // Email Group
    {
      id: emailGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Email Group',
      parentId: step1Id,
      hierarchyName: getHierarchyName('div'),
      className: quoteFormGroupClass,
      styles: {},
      children: [emailLabelId, emailInputId],
      text: '',
      attributes: {
        'data-element': 'form-group'
      }
    },
    
    // Email Label
    {
      id: emailLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Email Label',
      parentId: emailGroupId,
      hierarchyName: getHierarchyName('label'),
      className: quoteFormLabelClass,
      styles: {},
      children: [],
      text: 'Email Address *',
      attributes: {
        'data-element': 'form-label',
        'for': 'quote-email'
      }
    },
    
    // Email Input
    {
      id: emailInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Email Input',
      parentId: emailGroupId,
      hierarchyName: getHierarchyName('input'),
      className: quoteFormInputClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'form-input',
        'type': 'email',
        'id': 'quote-email',
        'name': 'email',
        'placeholder': 'Enter your email address',
        'required': 'true'
      }
    },

    // Phone Group
    {
      id: phoneGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Phone Group',
      parentId: step1Id,
      hierarchyName: getHierarchyName('div'),
      className: quoteFormGroupClass,
      styles: {},
      children: [phoneLabelId, phoneInputId],
      text: '',
      attributes: {
        'data-element': 'form-group'
      }
    },
    
    // Phone Label
    {
      id: phoneLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Phone Label',
      parentId: phoneGroupId,
      hierarchyName: getHierarchyName('label'),
      className: quoteFormLabelClass,
      styles: {},
      children: [],
      text: 'Phone Number *',
      attributes: {
        'data-element': 'form-label',
        'for': 'quote-phone'
      }
    },
    
    // Phone Input
    {
      id: phoneInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Phone Input',
      parentId: phoneGroupId,
      hierarchyName: getHierarchyName('input'),
      className: quoteFormInputClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'form-input',
        'type': 'tel',
        'id': 'quote-phone',
        'name': 'phone',
        'placeholder': '+92 300 1234567',
        'required': 'true'
      }
    },

    // Address Group
    {
      id: addressGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Address Group',
      parentId: step1Id,
      hierarchyName: getHierarchyName('div'),
      className: quoteFormGroupClass,
      styles: {},
      children: [addressLabelId, addressInputId],
      text: '',
      attributes: {
        'data-element': 'form-group'
      }
    },
    
    // Address Label
    {
      id: addressLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Address Label',
      parentId: addressGroupId,
      hierarchyName: getHierarchyName('label'),
      className: quoteFormLabelClass,
      styles: {},
      children: [],
      text: 'Property Address *',
      attributes: {
        'data-element': 'form-label',
        'for': 'quote-address'
      }
    },
    
    // Address Input
    {
      id: addressInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'textarea',
      name: 'Address Input',
      parentId: addressGroupId,
      hierarchyName: getHierarchyName('textarea'),
      className: quoteFormTextareaClass,
      styles: { minHeight: '80px' },
      children: [],
      text: '',
      attributes: {
        'data-element': 'form-textarea',
        'id': 'quote-address',
        'name': 'address',
        'placeholder': 'Enter complete property address including city and postal code',
        'required': 'true',
        'rows': '3'
      }
    },

    // Step 2 - Property Information
    {
      id: step2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Step 2 - Property Information',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: quoteStepClass,
      styles: {},
      children: [step2TitleId, propertyTypeGroupId, roofTypeGroupId, propertyAgeGroupId, roofAreaGroupId],
      text: '',
      attributes: {
        'data-element': 'quote-step',
        'data-step': '2'
      }
    },
    
    // Step 2 Title
    {
      id: step2TitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Step 2 Title',
      parentId: step2Id,
      hierarchyName: getHierarchyName('h3'),
      className: quoteStepTitleClass,
      styles: {},
      children: [],
      text: '2. Property Information',
      attributes: {
        'data-element': 'quote-step-title'
      }
    },

    // Property Type Group
    {
      id: propertyTypeGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Property Type Group',
      parentId: step2Id,
      hierarchyName: getHierarchyName('div'),
      className: quoteFormGroupClass,
      styles: {},
      children: [propertyTypeLabelId, propertyTypeSelectId],
      text: '',
      attributes: {
        'data-element': 'form-group'
      }
    },
    
    // Property Type Label
    {
      id: propertyTypeLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Property Type Label',
      parentId: propertyTypeGroupId,
      hierarchyName: getHierarchyName('label'),
      className: quoteFormLabelClass,
      styles: {},
      children: [],
      text: 'Property Type *',
      attributes: {
        'data-element': 'form-label',
        'for': 'quote-property-type'
      }
    },
    
    // Property Type Select
    {
      id: propertyTypeSelectId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'dropdown',
      name: 'Property Type Select',
      parentId: propertyTypeGroupId,
      hierarchyName: getHierarchyName('select'),
      className: quoteFormSelectClass,
      styles: {},
      children: [propertyTypeOption0Id, propertyTypeOption1Id, propertyTypeOption2Id, propertyTypeOption3Id, propertyTypeOption4Id, propertyTypeOption5Id, propertyTypeOption6Id, propertyTypeOption7Id, propertyTypeOption8Id],
      text: '',
      attributes: {
        'data-element': 'form-select',
        'id': 'quote-property-type',
        'name': 'property_type',
        'required': 'true'
      }
    },

    // Property Type Option 0
    {
      id: propertyTypeOption0Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'Property Type Option 0',
      parentId: propertyTypeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: 'Select a property type',
      attributes: {
        'value': ''
      }
    },

    // Property Type Option 1
    {
      id: propertyTypeOption1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'Property Type Option 1',
      parentId: propertyTypeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: 'Residential House',
      attributes: {
        'value': 'residential-house'
      }
    },

    // Property Type Option 2
    {
      id: propertyTypeOption2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'Property Type Option 2',
      parentId: propertyTypeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: 'Residential Apartment',
      attributes: {
        'value': 'residential-apartment'
      }
    },

    // Property Type Option 3
    {
      id: propertyTypeOption3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'Property Type Option 3',
      parentId: propertyTypeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: 'Commercial Office',
      attributes: {
        'value': 'commercial-office'
      }
    },

    // Property Type Option 4
    {
      id: propertyTypeOption4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'Property Type Option 4',
      parentId: propertyTypeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: 'Commercial Retail',
      attributes: {
        'value': 'commercial-retail'
      }
    },

    // Property Type Option 5
    {
      id: propertyTypeOption5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'Property Type Option 5',
      parentId: propertyTypeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: 'Industrial Warehouse',
      attributes: {
        'value': 'industrial-warehouse'
      }
    },

    // Property Type Option 6
    {
      id: propertyTypeOption6Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'Property Type Option 6',
      parentId: propertyTypeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: 'Industrial Factory',
      attributes: {
        'value': 'industrial-factory'
      }
    },

    // Property Type Option 7
    {
      id: propertyTypeOption7Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'Property Type Option 7',
      parentId: propertyTypeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: 'Agricultural',
      attributes: {
        'value': 'agricultural'
      }
    },

    // Property Type Option 8
    {
      id: propertyTypeOption8Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'Property Type Option 8',
      parentId: propertyTypeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: 'Other',
      attributes: {
        'value': 'other'
      }
    },

    // Roof Type Group
    {
      id: roofTypeGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Roof Type Group',
      parentId: step2Id,
      hierarchyName: getHierarchyName('div'),
      className: quoteFormGroupClass,
      styles: {},
      children: [roofTypeLabelId, roofTypeSelectId],
      text: '',
      attributes: {
        'data-element': 'form-group'
      }
    },
    
    // Roof Type Label
    {
      id: roofTypeLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Roof Type Label',
      parentId: roofTypeGroupId,
      hierarchyName: getHierarchyName('label'),
      className: quoteFormLabelClass,
      styles: {},
      children: [],
      text: 'Roof Type *',
      attributes: {
        'data-element': 'form-label',
        'for': 'quote-roof-type'
      }
    },
    
    // Roof Type Select
    {
      id: roofTypeSelectId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'dropdown',
      name: 'Roof Type Select',
      parentId: roofTypeGroupId,
      hierarchyName: getHierarchyName('select'),
      className: quoteFormSelectClass,
      styles: {},
      children: [roofTypeOption0Id, roofTypeOption1Id, roofTypeOption2Id, roofTypeOption3Id, roofTypeOption4Id, roofTypeOption5Id, roofTypeOption6Id, roofTypeOption7Id],
      text: '',
      attributes: {
        'data-element': 'form-select',
        'id': 'quote-roof-type',
        'name': 'roof_type',
        'required': 'true'
      }
    },

    // Roof Type Option 0
    {
      id: roofTypeOption0Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'Roof Type Option 0',
      parentId: roofTypeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: 'Select a roof type',
      attributes: {
        'value': ''
      }
    },

    // Roof Type Option 1
    {
      id: roofTypeOption1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'Roof Type Option 1',
      parentId: roofTypeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: 'Flat Roof',
      attributes: {
        'value': 'flat'
      }
    },

    // Roof Type Option 2
    {
      id: roofTypeOption2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'Roof Type Option 2',
      parentId: roofTypeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: 'Pitched/Sloped Roof',
      attributes: {
        'value': 'pitched'
      }
    },

    // Roof Type Option 3
    {
      id: roofTypeOption3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'Roof Type Option 3',
      parentId: roofTypeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: 'Metal Roof',
      attributes: {
        'value': 'metal'
      }
    },

    // Roof Type Option 4
    {
      id: roofTypeOption4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'Roof Type Option 4',
      parentId: roofTypeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: 'Tile Roof',
      attributes: {
        'value': 'tile'
      }
    },

    // Roof Type Option 5
    {
      id: roofTypeOption5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'Roof Type Option 5',
      parentId: roofTypeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: 'Concrete Roof',
      attributes: {
        'value': 'concrete'
      }
    },

    // Roof Type Option 6
    {
      id: roofTypeOption6Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'Roof Type Option 6',
      parentId: roofTypeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: 'Asphalt Shingles',
      attributes: {
        'value': 'asphalt'
      }
    },

    // Roof Type Option 7
    {
      id: roofTypeOption7Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'Roof Type Option 7',
      parentId: roofTypeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: 'Other',
      attributes: {
        'value': 'other'
      }
    },

    // Property Age Group
    {
      id: propertyAgeGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Property Age Group',
      parentId: step2Id,
      hierarchyName: getHierarchyName('div'),
      className: quoteFormGroupClass,
      styles: {},
      children: [propertyAgeLabelId, propertyAgeSelectId],
      text: '',
      attributes: {
        'data-element': 'form-group'
      }
    },
    
    // Property Age Label
    {
      id: propertyAgeLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Property Age Label',
      parentId: propertyAgeGroupId,
      hierarchyName: getHierarchyName('label'),
      className: quoteFormLabelClass,
      styles: {},
      children: [],
      text: 'Property Age',
      attributes: {
        'data-element': 'form-label',
        'for': 'quote-property-age'
      }
    },
    
    // Property Age Select
    {
      id: propertyAgeSelectId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'dropdown',
      name: 'Property Age Select',
      parentId: propertyAgeGroupId,
      hierarchyName: getHierarchyName('select'),
      className: quoteFormSelectClass,
      styles: {},
      children: [propertyAgeOption0Id, propertyAgeOption1Id, propertyAgeOption2Id, propertyAgeOption3Id, propertyAgeOption4Id],
      text: '',
      attributes: {
        'data-element': 'form-select',
        'id': 'quote-property-age',
        'name': 'property_age'
      }
    },

    // Property Age Option 0
    {
      id: propertyAgeOption0Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'Property Age Option 0',
      parentId: propertyAgeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: 'Select a property age',
      attributes: {
        'value': ''
      }
    },

    // Property Age Option 1
    {
      id: propertyAgeOption1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'Property Age Option 1',
      parentId: propertyAgeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: 'New (0-5 years)',
      attributes: {
        'value': 'new'
      }
    },

    // Property Age Option 2
    {
      id: propertyAgeOption2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'Property Age Option 2',
      parentId: propertyAgeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: 'Recent (6-15 years)',
      attributes: {
        'value': 'recent'
      }
    },

    // Property Age Option 3
    {
      id: propertyAgeOption3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'Property Age Option 3',
      parentId: propertyAgeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: 'Established (16-30 years)',
      attributes: {
        'value': 'established'
      }
    },

    // Property Age Option 4
    {
      id: propertyAgeOption4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'Property Age Option 4',
      parentId: propertyAgeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: 'Older (30+ years)',
      attributes: {
        'value': 'older'
      }
    },

    // Roof Area Group
    {
      id: roofAreaGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Roof Area Group',
      parentId: step2Id,
      hierarchyName: getHierarchyName('div'),
      className: quoteFormGroupClass,
      styles: {},
      children: [roofAreaLabelId, roofAreaInputId],
      text: '',
      attributes: {
        'data-element': 'form-group'
      }
    },
    
    // Roof Area Label
    {
      id: roofAreaLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Roof Area Label',
      parentId: roofAreaGroupId,
      hierarchyName: getHierarchyName('label'),
      className: quoteFormLabelClass,
      styles: {},
      children: [],
      text: 'Available Roof Area (sq ft)',
      attributes: {
        'data-element': 'form-label',
        'for': 'quote-roof-area'
      }
    },
    
    // Roof Area Input
    {
      id: roofAreaInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Roof Area Input',
      parentId: roofAreaGroupId,
      hierarchyName: getHierarchyName('input'),
      className: quoteFormInputClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'form-input',
        'type': 'number',
        'id': 'quote-roof-area',
        'name': 'roof_area',
        'placeholder': 'e.g., 1000',
        'min': '100'
      }
    },

    // Step 3 - Energy Requirements
    {
      id: step3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Step 3 - Energy Requirements',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: quoteStepClass,
      styles: {},
      children: [step3TitleId, monthlyBillGroupId, energyUsageGroupId, systemSizeGroupId],
      text: '',
      attributes: {
        'data-element': 'quote-step',
        'data-step': '3'
      }
    },
    
    // Step 3 Title
    {
      id: step3TitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Step 3 Title',
      parentId: step3Id,
      hierarchyName: getHierarchyName('h3'),
      className: quoteStepTitleClass,
      styles: {},
      children: [],
      text: '3. Energy Requirements',
      attributes: {
        'data-element': 'quote-step-title'
      }
    },

    // Monthly Bill Group
    {
      id: monthlyBillGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Monthly Bill Group',
      parentId: step3Id,
      hierarchyName: getHierarchyName('div'),
      className: quoteFormGroupClass,
      styles: {},
      children: [monthlyBillLabelId, monthlyBillInputId],
      text: '',
      attributes: {
        'data-element': 'form-group'
      }
    },
    
    // Monthly Bill Label
    {
      id: monthlyBillLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Monthly Bill Label',
      parentId: monthlyBillGroupId,
      hierarchyName: getHierarchyName('label'),
      className: quoteFormLabelClass,
      styles: {},
      children: [],
      text: 'Average Monthly Electricity Bill (PKR) *',
      attributes: {
        'data-element': 'form-label',
        'for': 'quote-monthly-bill'
      }
    },
    
    // Monthly Bill Input
    {
      id: monthlyBillInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Monthly Bill Input',
      parentId: monthlyBillGroupId,
      hierarchyName: getHierarchyName('input'),
      className: quoteFormInputClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'form-input',
        'type': 'number',
        'id': 'quote-monthly-bill',
        'name': 'monthly_bill',
        'placeholder': 'e.g., 25000',
        'required': 'true',
        'min': '1000'
      }
    },

    // Energy Usage Group
    {
      id: energyUsageGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Energy Usage Group',
      parentId: step3Id,
      hierarchyName: getHierarchyName('div'),
      className: quoteFormGroupClass,
      styles: {},
      children: [energyUsageLabelId, energyUsageInputId],
      text: '',
      attributes: {
        'data-element': 'form-group'
      }
    },
    
    // Energy Usage Label
    {
      id: energyUsageLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Energy Usage Label',
      parentId: energyUsageGroupId,
      hierarchyName: getHierarchyName('label'),
      className: quoteFormLabelClass,
      styles: {},
      children: [],
      text: 'Monthly Energy Usage (kWh)',
      attributes: {
        'data-element': 'form-label',
        'for': 'quote-energy-usage'
      }
    },
    
    // Energy Usage Input
    {
      id: energyUsageInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Energy Usage Input',
      parentId: energyUsageGroupId,
      hierarchyName: getHierarchyName('input'),
      className: quoteFormInputClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'form-input',
        'type': 'number',
        'id': 'quote-energy-usage',
        'name': 'energy_usage',
        'placeholder': 'e.g., 800 (check your electricity bill)',
        'min': '100'
      }
    },

    // System Size Group
    {
      id: systemSizeGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'System Size Group',
      parentId: step3Id,
      hierarchyName: getHierarchyName('div'),
      className: quoteFormGroupClass,
      styles: {},
      children: [systemSizeLabelId, systemSizeSelectId],
      text: '',
      attributes: {
        'data-element': 'form-group'
      }
    },
    
    // System Size Label
    {
      id: systemSizeLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'System Size Label',
      parentId: systemSizeGroupId,
      hierarchyName: getHierarchyName('label'),
      className: quoteFormLabelClass,
      styles: {},
      children: [],
      text: 'Preferred System Size',
      attributes: {
        'data-element': 'form-label',
        'for': 'quote-system-size'
      }
    },
    
    // System Size Select
    {
      id: systemSizeSelectId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'dropdown',
      name: 'System Size Select',
      parentId: systemSizeGroupId,
      hierarchyName: getHierarchyName('select'),
      className: quoteFormSelectClass,
      styles: {},
      children: [systemSizeOption0Id, systemSizeOption1Id, systemSizeOption2Id, systemSizeOption3Id, systemSizeOption4Id, systemSizeOption5Id, systemSizeOption6Id, systemSizeOption7Id],
      text: '',
      attributes: {
        'data-element': 'form-select',
        'id': 'quote-system-size',
        'name': 'system_size'
      }
    },

    // System Size Option 0
    {
      id: systemSizeOption0Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'System Size Option 0',
      parentId: systemSizeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: 'Select a system size',
      attributes: {
        'value': ''
      }
    },

    // System Size Option 1
    {
      id: systemSizeOption1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'System Size Option 1',
      parentId: systemSizeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: '5 kW (Small residential)',
      attributes: {
        'value': '5kw'
      }
    },

    // System Size Option 2
    {
      id: systemSizeOption2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'System Size Option 2',
      parentId: systemSizeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: '10 kW (Medium residential)',
      attributes: {
        'value': '10kw'
      }
    },

    // System Size Option 3
    {
      id: systemSizeOption3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'System Size Option 3',
      parentId: systemSizeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: '15 kW (Large residential)',
      attributes: {
        'value': '15kw'
      }
    },

    // System Size Option 4
    {
      id: systemSizeOption4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'System Size Option 4',
      parentId: systemSizeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: '20 kW (Small commercial)',
      attributes: {
        'value': '20kw'
      }
    },

    // System Size Option 5
    {
      id: systemSizeOption5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'System Size Option 5',
      parentId: systemSizeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: '50 kW (Medium commercial)',
      attributes: {
        'value': '50kw'
      }
    },

    // System Size Option 6
    {
      id: systemSizeOption6Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'System Size Option 6',
      parentId: systemSizeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: '100 kW+ (Large commercial/Industrial)',
      attributes: {
        'value': '100kw'
      }
    },

    // System Size Option 7
    {
      id: systemSizeOption7Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'System Size Option 7',
      parentId: systemSizeSelectId,
      hierarchyName: getHierarchyName('option'),
      className: quoteFormOptionClass,
      styles: {},
      children: [],
      text: 'Custom size',
      attributes: {
        'value': 'custom'
      }
    },

    // Step 4 - Service Selection & Additional Services
    {
      id: step4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Step 4 - Service Selection',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: quoteStepClass,
      styles: {},
      children: [step4TitleId, servicesGroupId, cctvCheckboxId, doorLockCheckboxId, intercomCheckboxId, maintenanceCheckboxId, notesGroupId],
      text: '',
      attributes: {
        'data-element': 'quote-step',
        'data-step': '4'
      }
    },
    
    // Step 4 Title
    {
      id: step4TitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Step 4 Title',
      parentId: step4Id,
      hierarchyName: getHierarchyName('h3'),
      className: quoteStepTitleClass,
      styles: {},
      children: [],
      text: '4. Service Selection & Additional Requirements',
      attributes: {
        'data-element': 'quote-step-title'
      }
    },

    // Primary Service Group
    {
      id: servicesGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Primary Service Group',
      parentId: step4Id,
      hierarchyName: getHierarchyName('div'),
      className: quoteFormGroupClass,
      styles: {},
      children: [servicesLabelId],
      text: '',
      attributes: {
        'data-element': 'form-group'
      }
    },
    
    // Services Label
    {
      id: servicesLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Services Label',
      parentId: servicesGroupId,
      hierarchyName: getHierarchyName('label'),
      className: quoteFormLabelClass,
      styles: { marginBottom: '15px', display: 'block' },
      children: [],
      text: 'Additional Services (Select all that apply):',
      attributes: {
        'data-element': 'form-label'
      }
    },

    // CCTV Checkbox
    {
      id: cctvCheckboxId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'CCTV Checkbox',
      parentId: step4Id,
      hierarchyName: getHierarchyName('label'),
      className: quoteFormCheckboxClass,
      styles: {},
      children: [cctvInputId, cctvSpanId],
      text: '',
      attributes: {
        'data-element': 'form-checkbox'
      }
    },

    // CCTV Input
    {
      id: cctvInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'CCTV Input',
      parentId: cctvCheckboxId,
      hierarchyName: getHierarchyName('input'),
      styles: { marginRight: '10px' },
      children: [],
      text: '',
      attributes: {
        'type': 'checkbox',
        'name': 'services[]',
        'value': 'cctv'
      }
    },

    // CCTV Span
    {
      id: cctvSpanId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'CCTV Span',
      parentId: cctvCheckboxId,
      hierarchyName: getHierarchyName('span'),
      styles: {},
      children: [],
      text: '📹 CCTV Installation & Repair - Complete surveillance system setup',
      attributes: {}
    },

    // Door Lock Checkbox
    {
      id: doorLockCheckboxId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Door Lock Checkbox',
      parentId: step4Id,
      hierarchyName: getHierarchyName('label'),
      className: quoteFormCheckboxClass,
      styles: {},
      children: [doorLockInputId, doorLockSpanId],
      text: '',
      attributes: {
        'data-element': 'form-checkbox'
      }
    },

    // Door Lock Input
    {
      id: doorLockInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Door Lock Input',
      parentId: doorLockCheckboxId,
      hierarchyName: getHierarchyName('input'),
      styles: { marginRight: '10px' },
      children: [],
      text: '',
      attributes: {
        'type': 'checkbox',
        'name': 'services[]',
        'value': 'door-lock'
      }
    },

    // Door Lock Span
    {
      id: doorLockSpanId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Door Lock Span',
      parentId: doorLockCheckboxId,
      hierarchyName: getHierarchyName('span'),
      styles: {},
      children: [],
      text: '🔐 Electrical Door Lock Systems - Smart access control solutions',
      attributes: {}
    },

    // Intercom Checkbox
    {
      id: intercomCheckboxId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Intercom Checkbox',
      parentId: step4Id,
      hierarchyName: getHierarchyName('label'),
      className: quoteFormCheckboxClass,
      styles: {},
      children: [intercomInputId, intercomSpanId],
      text: '',
      attributes: {
        'data-element': 'form-checkbox'
      }
    },

    // Intercom Input
    {
      id: intercomInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Intercom Input',
      parentId: intercomCheckboxId,
      hierarchyName: getHierarchyName('input'),
      styles: { marginRight: '10px' },
      children: [],
      text: '',
      attributes: {
        'type': 'checkbox',
        'name': 'services[]',
        'value': 'intercom'
      }
    },

    // Intercom Span
    {
      id: intercomSpanId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Intercom Span',
      parentId: intercomCheckboxId,
      hierarchyName: getHierarchyName('span'),
      styles: {},
      children: [],
      text: '📞 Intercoms & PABX Systems - Communication infrastructure',
      attributes: {}
    },

    // Maintenance Checkbox
    {
      id: maintenanceCheckboxId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Maintenance Checkbox',
      parentId: step4Id,
      hierarchyName: getHierarchyName('label'),
      className: quoteFormCheckboxClass,
      styles: {},
      children: [maintenanceInputId, maintenanceSpanId],
      text: '',
      attributes: {
        'data-element': 'form-checkbox'
      }
    },

    // Maintenance Input
    {
      id: maintenanceInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Maintenance Input',
      parentId: maintenanceCheckboxId,
      hierarchyName: getHierarchyName('input'),
      styles: { marginRight: '10px' },
      children: [],
      text: '',
      attributes: {
        'type': 'checkbox',
        'name': 'services[]',
        'value': 'maintenance'
      }
    },

    // Maintenance Span
    {
      id: maintenanceSpanId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Maintenance Span',
      parentId: maintenanceCheckboxId,
      hierarchyName: getHierarchyName('span'),
      styles: {},
      children: [],
      text: '🔧 Solar System Maintenance - Regular cleaning and performance monitoring',
      attributes: {}
    },

    // Notes Group
    {
      id: notesGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Notes Group',
      parentId: step4Id,
      hierarchyName: getHierarchyName('div'),
      className: quoteFormGroupClass,
      styles: {},
      children: [notesLabelId, notesTextareaId],
      text: '',
      attributes: {
        'data-element': 'form-group'
      }
    },
    
    // Notes Label
    {
      id: notesLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Notes Label',
      parentId: notesGroupId,
      hierarchyName: getHierarchyName('label'),
      className: quoteFormLabelClass,
      styles: {},
      children: [],
      text: 'Additional Requirements or Questions',
      attributes: {
        'data-element': 'form-label',
        'for': 'quote-notes'
      }
    },
    
    // Notes Textarea
    {
      id: notesTextareaId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'textarea',
      name: 'Notes Textarea',
      parentId: notesGroupId,
      hierarchyName: getHierarchyName('textarea'),
      className: quoteFormTextareaClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'form-textarea',
        'id': 'quote-notes',
        'name': 'notes',
        'placeholder': 'Please share any specific requirements, timeline preferences, budget considerations, or questions you have about our services...',
        'rows': '4'
      }
    },

    // Submit Button
    {
      id: submitButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Submit Button',
      parentId: formId,
      hierarchyName: getHierarchyName('button'),
      className: quoteFormButtonClass,
      styles: {},
      children: [],
      text: 'Get My Free Detailed Quote',
      attributes: {
        'data-element': 'form-button',
        'type': 'submit'
      }
    },

    // Success Message
    {
      id: successMessageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Success Message',
      parentId: formWrapperId,
      hierarchyName: getHierarchyName('div'),
      className: quoteFormSuccessClass,
      styles: {},
      children: [],
      text: 'Thank you! Your detailed quote request has been submitted successfully. Our solar experts will analyze your requirements and contact you within 24 hours with a comprehensive proposal including pricing, system design, and installation timeline.',
      attributes: {
        'data-element': 'form-success-message'
      }
    },

    // Error Message
    {
      id: errorMessageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Error Message',
      parentId: formWrapperId,
      hierarchyName: getHierarchyName('div'),
      className: quoteFormErrorClass,
      styles: {},
      children: [],
      text: 'There was an error submitting your quote request. Please try again or call us directly at +92 300 1234567 for immediate assistance.',
      attributes: {
        'data-element': 'form-error-message'
      }
    }
  ];

  return {
    quoteSection: newElements[0],
    elements: [...newElements, ...additionalElements]
  };
};